import { Fragment, useState, useCallback } from "react";
import { useUser } from "hooks"; // Custom hook for user data
import notification from "utilities/notification"; // For success/error messages
import { API_ENDPOINTS, request } from "utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, // Icon for name
  faEnvelope, // Icon for email
  faPhone, // Icon for phone
  faEdit, // Icon for edit button
  faSave, // Icon for save button
} from "@fortawesome/free-solid-svg-icons";

const AccountDetails = () => {
  const { user } = useUser(); // Get user data from useUser hook

  // Initialize form data with user data or defaults
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });

  // State for form editing
  const [isEditing, setIsEditing] = useState(false);

  // Handle input change
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        if (typeof request.update !== "function") {
          throw new Error("request.update is not a function");
        }
        const response = await request.update(API_ENDPOINTS.USER_UPDATE, formData);
        if (response.success) {
          notification("success", "Account details updated successfully!");
          setIsEditing(false);
        } else {
          throw new Error(response.message || "Update failed");
        }
      } catch (error) {
        notification("error", error.message || "Failed to update account details.");
      }
    },
    [formData]
  );

  // Toggle edit mode
  const toggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  return (
    <Fragment>
      <div className="mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">Account Details</h5>
            <button
              className="btn btn-link p-0"
              onClick={toggleEdit}
              title={isEditing ? "Save changes" : "Edit details"}
            >
              <FontAwesomeIcon icon={isEditing ? faSave : faEdit} />
            </button>
          </div>

          {/* Name */}
          <div className="d-flex align-items-start mb-3">
            <FontAwesomeIcon icon={faUser} className="me-3 mt-1" />
            <div className="flex-grow-1">
              <h6>Name</h6>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your name"
                />
              ) : (
                <p className="mb-0">{formData.name || "Not provided"}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="d-flex align-items-start mb-3">
            <FontAwesomeIcon icon={faEnvelope} className="me-3 mt-1" />
            <div className="flex-grow-1">
              <h6>Email</h6>
              {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your email"
                />
              ) : (
                <p className="mb-0">{formData.email || "Not provided"}</p>
              )}
            </div>
          </div>

          {/* Phone */}
          <div className="d-flex align-items-start mb-3">
            <FontAwesomeIcon icon={faPhone} className="me-3 mt-1" />
            <div className="flex-grow-1">
              <h6>Phone</h6>
              {isEditing ? (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your phone number"
                />
              ) : (
                <p className="mb-0">{formData.phone || "Not provided"}</p>
              )}
            </div>
          </div>

          {/* Save Button (visible only in edit mode) */}
          {isEditing && (
            <div className="text-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default AccountDetails;